import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { format } from "date-fns";
import {
  Loader2,
  ArrowLeft,
  Plus,
  Trash2,
  Save,
  Users,
  Calendar as CalIcon,
  Mail,
  Send,
  X,
  Download,
  Upload,
  FileText,
  Video,
  HelpCircle,
  Eye,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { hasAdminAccess } from "@/lib/admin-access";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { TopicPollPanel } from "@/components/admin/TopicPollPanel";
import { notifySessionScheduled } from "@/lib/notify-session-scheduled.functions";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { referralBadge } from "@/lib/referral-tracker";

export const Route = createFileRoute("/admin/live-sessions")({
  head: () => ({
    meta: [{ title: "Live sessions - Admin | WeLivdIt" }, { name: "robots", content: "noindex, nofollow" }],
  }),
  component: AdminLiveSessions,
});

type SessionStatus = "planning" | "scheduled" | "completed";

type Session = {
  id: string;
  title: string;
  description: string | null;
  starts_at: string | null;
  duration_minutes: number;
  join_url: string | null;
  location: string | null;
  is_published: boolean;
  status: SessionStatus;
  registration_link: string | null;
  created_at: string;
  notes_html: string | null;
  notes_doc_url: string | null;
  replay_url: string | null;
  notes_published: boolean;
  peak_viewers: number;
};

type Attendee = {
  id: string;
  session_id: string | null;
  email: string;
  name: string | null;
  whatsapp: string | null;
  target_consulate: string | null;
  question: string | null;
  volunteer_status: boolean | null;
  source: string | null;
  created_at: string;
};

type PaidPayment = {
  plan_name: string;
  buyer_email: string | null;
  amount_paise: number;
  created_at: string;
  converted_from_free_session: boolean | null;
};

function toLocalInputValue(iso: string | null): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const off = d.getTimezoneOffset();
    const local = new Date(d.getTime() - off * 60_000);
    return local.toISOString().slice(0, 16);
  } catch {
    return "";
  }
}

function fromLocalInputValue(v: string): string | null {
  if (!v || !v.trim()) return null;
  try {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d.toISOString();
  } catch {
    return null;
  }
}

async function uploadSessionDoc(file: File): Promise<string> {
  const safe = file.name.replace(/[^\w.\-]+/g, "_").slice(-80);
  const path = `${Date.now()}-${safe}`;
  const { error } = await supabase.storage.from("session-notes").upload(path, file, { upsert: false });
  if (error) throw new Error(error.message);
  const { data, error: signErr } = await supabase.storage.from("session-notes").createSignedUrl(path, 315_360_000);
  if (signErr || !data?.signedUrl) throw new Error(signErr?.message ?? "Could not create a link for the file");
  return data.signedUrl;
}

function AdminLiveSessions() {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [payments, setPayments] = useState<PaidPayment[]>([]);
  const [loading, setLoading] = useState(true);

  const [draft, setDraft] = useState({
    title: "",
    description: "",
    starts_at: "",
    duration_minutes: 0,
    join_url: "",
    location: "Online (Google Meet link shared after signup)",
    is_published: false,
    status: "planning" as SessionStatus,
    registration_link: "",
    notes_html: "",
    notes_doc_url: "",
    replay_url: "",
    notes_published: false,
  });
  const [creating, setCreating] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const draftFileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    (async () => {
      const { data: sess } = await supabase.auth.getSession();
      if (!sess.session || !(await hasAdminAccess(sess.session))) {
        navigate({ to: "/auth" });
        return;
      }
      setAuthChecked(true);
    })();
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    const [sessRes, regRes, payRes] = await Promise.all([
      supabase.from("live_sessions").select("*").order("created_at", { ascending: false }),
      supabase.from("live_session_reminders").select("*").order("created_at", { ascending: false }),
      supabase
        .from("payments")
        .select("plan_name, buyer_email, amount_paise, created_at, converted_from_free_session")
        .eq("status", "paid"),
    ]);
    setSessions((sessRes.data as Session[]) ?? []);
    setAttendees((regRes.data as Attendee[]) ?? []);
    setPayments((payRes.data as PaidPayment[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (authChecked) load();
  }, [authChecked, load]);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      if (!draft.title.trim()) {
        toast.error("Title is required");
        return;
      }

      const { error } = await supabase.from("live_sessions").insert({
        title: draft.title.trim(),
        description: draft.description.trim() || null,
        starts_at: fromLocalInputValue(draft.starts_at),
        duration_minutes: Number(draft.duration_minutes) || 0,
        join_url: draft.join_url.trim() || null,
        location: draft.location.trim() || null,
        is_published: draft.is_published,
        status: draft.status,
        registration_link: draft.registration_link.trim() || null,
        notes_html: draft.notes_html.trim() || null,
        notes_doc_url: draft.notes_doc_url.trim() || null,
        replay_url: draft.replay_url.trim() || null,
        notes_published: draft.notes_published,
      });

      if (error) {
        toast.error("Couldn't create session", { description: error.message });
        return;
      }
      toast.success("Session created");
      setDraft({
        ...draft,
        title: "",
        description: "",
        starts_at: "",
        duration_minutes: 0,
        join_url: "",
        registration_link: "",
        notes_html: "",
        notes_doc_url: "",
        replay_url: "",
        notes_published: false,
      });
      load();
    } catch (err: any) {
      toast.error("Couldn't create session", { description: err?.message ?? String(err) });
    } finally {
      setCreating(false);
    }
  };

  const removeSession = async (id: string) => {
    if (!confirm("Delete this session?")) return;
    const { error } = await supabase.from("live_sessions").delete().eq("id", id);
    if (error) {
      toast.error("Delete failed", { description: error.message });
      return;
    }
    toast.success("Session deleted");
    load();
  };

  const statsBySession = useMemo(() => {
    const map: Record<string, { registered: number; questions: number }> = {};
    for (const a of attendees) {
      const key = a.session_id ?? "__general__";
      const entry = (map[key] ||= { registered: 0, questions: 0 });
      entry.registered += 1;
      if (a.question && a.question.trim()) entry.questions += 1;
    }
    return map;
  }, [attendees]);

  /** Email -> paid plan, used for the attendee table + revenue KPI. */
  const paidPlanByEmail = useMemo(() => {
    const map: Record<string, string> = {};
    for (const p of payments) {
      const email = (p.buyer_email || "").trim().toLowerCase();
      if (email) map[email] = p.plan_name;
    }
    return map;
  }, [payments]);

  const ytd = useMemo(() => {
    const year = new Date().getFullYear();
    const inYear = (iso: string) => new Date(iso).getFullYear() === year;
    const unique = new Set<string>();
    let registrations = 0;
    for (const a of attendees) {
      if (!inYear(a.created_at)) continue;
      registrations += 1;
      unique.add((a.email || "").toLowerCase());
    }
    const revenuePaise = payments
      .filter((p) => p.converted_from_free_session && inYear(p.created_at))
      .reduce((sum, p) => sum + (p.amount_paise || 0), 0);
    return { unique: unique.size, registrations, revenue: Math.round(revenuePaise / 100) };
  }, [attendees, payments]);

  if (!authChecked) {
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <section className="mx-auto max-w-5xl px-5 py-10">
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-foreground/60 hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to admin
      </Link>
      <h1 className="mt-4 font-display text-3xl font-semibold">Live Sessions</h1>
      <p className="mt-1 text-foreground/70 text-sm">
        Manage F1 visa prep sessions, publish notes, and track registered students.
      </p>

      {/* YTD SUMMARY METRICS */}
      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-border bg-card p-5">
          <p className="text-xs uppercase tracking-wider text-foreground/60">
            Total Unique Free Session Students (YTD)
          </p>
          <p className="mt-1 text-3xl font-semibold">{loading ? "—" : ytd.unique}</p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5">
          <p className="text-xs uppercase tracking-wider text-foreground/60">Total Session Registrations (YTD)</p>
          <p className="mt-1 text-3xl font-semibold">{loading ? "—" : ytd.registrations}</p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5">
          <p className="text-xs uppercase tracking-wider text-foreground/60">
            Revenue Generated from Free Sessions
          </p>
          <p className="mt-1 text-3xl font-semibold">
            {loading ? "—" : `₹${ytd.revenue.toLocaleString("en-IN")}`}
          </p>
          <p className="mt-1 text-[11px] text-foreground/50">Paid plans bought by past free-session attendees.</p>
        </div>
      </div>

      <Tabs defaultValue="sessions" className="mt-8">
        <TabsList>
          <TabsTrigger value="sessions">Sessions</TabsTrigger>
          <TabsTrigger value="attendees">Registered Attendees ({attendees.length})</TabsTrigger>
          <TabsTrigger value="poll">Poll & Questions</TabsTrigger>
        </TabsList>

        <TabsContent value="poll" className="mt-6">
          <TopicPollPanel />
        </TabsContent>

        <TabsContent value="sessions" className="mt-6">
          <form onSubmit={create} className="rounded-2xl border border-border bg-card p-6 space-y-4">
            <div className="flex items-center gap-2">
              <Plus className="h-4 w-4 text-primary" />
              <h2 className="font-semibold">New session</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="text-sm sm:col-span-2">
                <span className="text-xs font-medium text-foreground/70">Title *</span>
                <input
                  type="text"
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  maxLength={120}
                  placeholder="Cracking the 214(b) Trap & Funding Questions"
                  className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                />
              </label>
              <label className="text-sm">
                <span className="text-xs font-medium text-foreground/70">Status</span>
                <select
                  value={draft.status}
                  onChange={(e) => setDraft({ ...draft, status: e.target.value as SessionStatus })}
                  className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                >
                  <option value="planning">Planning</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="completed">Completed</option>
                </select>
              </label>
              <label className="text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-foreground/70">Starts at (Optional)</span>
                  {draft.starts_at && (
                    <button
                      type="button"
                      onClick={() => setDraft({ ...draft, starts_at: "" })}
                      className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-0.5"
                    >
                      <X className="h-3 w-3" /> Clear date
                    </button>
                  )}
                </div>
                <input
                  type="datetime-local"
                  value={draft.starts_at}
                  onChange={(e) => setDraft({ ...draft, starts_at: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                />
              </label>
              <label className="text-sm">
                <span className="text-xs font-medium text-foreground/70">Duration (min)</span>
                <input
                  type="number"
                  min={0}
                  max={240}
                  value={draft.duration_minutes}
                  onChange={(e) => setDraft({ ...draft, duration_minutes: Number(e.target.value) })}
                  className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                />
              </label>
              <label className="text-sm">
                <span className="text-xs font-medium text-foreground/70">Registration link</span>
                <input
                  type="url"
                  value={draft.registration_link}
                  onChange={(e) => setDraft({ ...draft, registration_link: e.target.value })}
                  placeholder="https://forms.gle/…"
                  className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                />
              </label>
              <label className="text-sm sm:col-span-2">
                <span className="text-xs font-medium text-foreground/70">Join URL (Meet/Zoom)</span>
                <input
                  type="url"
                  value={draft.join_url}
                  onChange={(e) => setDraft({ ...draft, join_url: e.target.value })}
                  placeholder="https://meet.google.com/…"
                  className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                />
              </label>
              <label className="text-sm sm:col-span-2">
                <span className="text-xs font-medium text-foreground/70">Description / agenda</span>
                <textarea
                  value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  rows={3}
                  maxLength={500}
                  className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none"
                />
              </label>

              <div className="sm:col-span-2 space-y-3 rounded-xl border border-dashed border-border p-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-primary">Deliverables</p>
                <div>
                  <span className="text-xs font-medium text-foreground/70">Meeting notes</span>
                  <div className="mt-1">
                    <RichTextEditor
                      value={draft.notes_html}
                      onChange={(html) => setDraft((d) => ({ ...d, notes_html: html }))}
                    />
                  </div>
                </div>
                <label className="text-sm block">
                  <span className="text-xs font-medium text-foreground/70">Replay video URL (Zoom / YouTube)</span>
                  <input
                    type="url"
                    value={draft.replay_url}
                    onChange={(e) => setDraft({ ...draft, replay_url: e.target.value })}
                    placeholder="https://youtube.com/watch?v=…"
                    className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                  />
                </label>
                <div className="text-sm">
                  <span className="text-xs font-medium text-foreground/70">Notes document (PDF / slides)</span>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <input
                      ref={draftFileRef}
                      type="file"
                      accept=".pdf,.ppt,.pptx,.doc,.docx"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setUploadingDoc(true);
                        try {
                          const url = await uploadSessionDoc(file);
                          setDraft((d) => ({ ...d, notes_doc_url: url }));
                          toast.success("Document uploaded");
                        } catch (err: any) {
                          toast.error("Upload failed", { description: err?.message });
                        } finally {
                          setUploadingDoc(false);
                          if (draftFileRef.current) draftFileRef.current.value = "";
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => draftFileRef.current?.click()}
                      disabled={uploadingDoc}
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-border hover:bg-accent disabled:opacity-60"
                    >
                      {uploadingDoc ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                      Upload document
                    </button>
                    {draft.notes_doc_url && (
                      <a
                        href={draft.notes_doc_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                      >
                        <FileText className="h-3.5 w-3.5" /> View attached file
                      </a>
                    )}
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={draft.notes_published}
                    onChange={(e) => setDraft({ ...draft, notes_published: e.target.checked })}
                    className="h-4 w-4"
                  />
                  <span>Publish Notes &amp; Replay to Public Hub</span>
                </label>
              </div>

              <label className="flex items-center gap-2 text-sm sm:col-span-2">
                <input
                  type="checkbox"
                  checked={draft.is_published}
                  onChange={(e) => setDraft({ ...draft, is_published: e.target.checked })}
                  className="h-4 w-4"
                />
                <span>Publish session (show on homepage banner &amp; public hub)</span>
              </label>
            </div>
            <button
              type="submit"
              disabled={creating}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-cta text-primary-foreground text-sm font-semibold hover:opacity-90 transition disabled:opacity-60"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Create session
            </button>
          </form>

          <h2 className="mt-10 font-semibold text-lg">All sessions</h2>
          {loading ? (
            <div className="mt-4 flex justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            </div>
          ) : sessions.length === 0 ? (
            <div className="mt-4 rounded-2xl border border-dashed border-border p-8 text-center text-sm text-foreground/60">
              No sessions yet.
            </div>
          ) : (
            <ul className="mt-4 space-y-4">
              {sessions.map((s) => (
                <SessionRow
                  key={s.id}
                  session={s}
                  registered={statsBySession[s.id]?.registered ?? 0}
                  questions={statsBySession[s.id]?.questions ?? 0}
                  onDelete={() => removeSession(s.id)}
                  onReload={load}
                />
              ))}
            </ul>
          )}
        </TabsContent>

        <TabsContent value="attendees" className="mt-6">
          <AttendeesPanel
            attendees={attendees}
            sessions={sessions}
            loading={loading}
            paidPlanByEmail={paidPlanByEmail}
          />
        </TabsContent>
      </Tabs>
    </section>
  );
}

function SessionRow({
  session,
  registered,
  questions,
  onDelete,
  onReload,
}: {
  session: Session;
  registered: number;
  questions: number;
  onDelete: () => void;
  onReload: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notify, setNotify] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const notify$ = useServerFn(notifySessionScheduled);
  const [edit, setEdit] = useState({
    title: session.title,
    description: session.description ?? "",
    starts_at: toLocalInputValue(session.starts_at),
    duration_minutes: session.duration_minutes,
    join_url: session.join_url ?? "",
    location: session.location ?? "",
    is_published: session.is_published,
    status: session.status,
    registration_link: session.registration_link ?? "",
    notes_html: session.notes_html ?? "",
    notes_doc_url: session.notes_doc_url ?? "",
    replay_url: session.replay_url ?? "",
    notes_published: session.notes_published,
    peak_viewers: session.peak_viewers ?? 0,
  });

  const past = session.starts_at ? new Date(session.starts_at).getTime() < Date.now() : false;

  const save = async () => {
    setSaving(true);
    try {
      if (!edit.title.trim()) {
        toast.error("Title is required");
        return;
      }

      const prevStatus = session.status;
      const patch = {
        title: edit.title.trim(),
        description: edit.description.trim() || null,
        starts_at: fromLocalInputValue(edit.starts_at),
        duration_minutes: Number(edit.duration_minutes) || 0,
        join_url: edit.join_url.trim() || null,
        location: edit.location.trim() || null,
        is_published: edit.is_published,
        status: edit.status,
        registration_link: edit.registration_link.trim() || null,
        notes_html: edit.notes_html.trim() || null,
        notes_doc_url: edit.notes_doc_url.trim() || null,
        replay_url: edit.replay_url.trim() || null,
        notes_published: edit.notes_published,
        peak_viewers: Number(edit.peak_viewers) || 0,
      };

      const { error } = await supabase.from("live_sessions").update(patch).eq("id", session.id);
      if (error) {
        toast.error("Update failed", { description: error.message });
        return;
      }
      toast.success("Saved");

      // Email registered students when the session flips to scheduled AND checkbox is on
      if (notify && edit.status === "scheduled" && prevStatus !== "scheduled") {
        try {
          const startsAt = patch.starts_at ? new Date(patch.starts_at) : null;
          const result = await notify$({
            data: {
              sessionId: session.id,
              sessionTitle: patch.title,
              description: patch.description || "",
              dateLabel: startsAt
                ? startsAt.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })
                : "Today / Date TBA",
              timeLabel: startsAt
                ? startsAt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
                : "Now",
              registrationUrl: patch.registration_link || patch.join_url || "https://welivdit.com",
            },
          });
          toast.success(
            `Registered students: ${result.waitlistQueued} queued (${result.waitlistSkipped} skipped, ${result.waitlistFailed} failed) · VIP: ${result.vipQueued} queued (${result.vipSkipped} skipped, ${result.vipFailed} failed)`,
          );
        } catch (err: any) {
          toast.error("Notification failed", { description: err?.message });
        }
      } else if (notify && edit.status !== "scheduled") {
        toast.message("Students not notified - status must be 'scheduled'");
      }

      setExpanded(false);
      onReload();
    } finally {
      setSaving(false);
    }
  };

  return (
    <li className="rounded-2xl border border-border bg-card overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 p-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold truncate">{session.title}</span>
            <StatusPill status={session.status} />
            {!session.is_published && (
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                Draft
              </span>
            )}
            {past && (
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border">
                Past
              </span>
            )}
          </div>
          <p className="mt-0.5 text-xs text-foreground/60">
            <CalIcon className="inline h-3.5 w-3.5 mr-1" />
            {session.starts_at ? `${format(new Date(session.starts_at), "EEE, MMM d · h:mm a")} · ` : "No date set · "}
            {session.duration_minutes} min
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-foreground/70">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-border">
              <Users className="h-3 w-3" /> {registered} registered
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-border">
              <Eye className="h-3 w-3" /> {session.peak_viewers ?? 0} peak viewers
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-border">
              <HelpCircle className="h-3 w-3" /> {questions} questions
            </span>
            <span
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border ${
                session.notes_published
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                  : "border-border text-muted-foreground"
              }`}
            >
              <FileText className="h-3 w-3" /> {session.notes_published ? "Notes published" : "Notes unpublished"}
            </span>
            {session.replay_url && (
              <a
                href={session.replay_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-border hover:bg-accent"
              >
                <Video className="h-3 w-3" /> Replay
              </a>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-xs px-3 py-1.5 rounded-full border border-border hover:bg-accent"
        >
          {expanded ? "Close" : "Edit Session & Notes"}
        </button>
        <button
          type="button"
          onClick={onDelete}
          aria-label="Delete session"
          className="text-xs px-3 py-1.5 rounded-full border border-red-200 text-red-700 hover:bg-red-50"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      {expanded && (
        <div className="border-t border-border p-4 grid sm:grid-cols-2 gap-3">
          <label className="text-sm sm:col-span-2">
            <span className="text-xs text-foreground/70">Title</span>
            <input
              type="text"
              value={edit.title}
              onChange={(e) => setEdit({ ...edit, title: e.target.value })}
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
          <label className="text-sm">
            <span className="text-xs text-foreground/70">Status</span>
            <select
              value={edit.status}
              onChange={(e) => setEdit({ ...edit, status: e.target.value as SessionStatus })}
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            >
              <option value="planning">Planning</option>
              <option value="scheduled">Scheduled</option>
              <option value="completed">Completed</option>
            </select>
          </label>
          <label className="text-sm">
            <div className="flex items-center justify-between">
              <span className="text-xs text-foreground/70">Starts at (Optional)</span>
              {edit.starts_at && (
                <button
                  type="button"
                  onClick={() => setEdit({ ...edit, starts_at: "" })}
                  className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-0.5"
                >
                  <X className="h-3 w-3" /> Clear date
                </button>
              )}
            </div>
            <input
              type="datetime-local"
              value={edit.starts_at}
              onChange={(e) => setEdit({ ...edit, starts_at: e.target.value })}
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
          <label className="text-sm">
            <span className="text-xs text-foreground/70">Duration (min)</span>
            <input
              type="number"
              min={0}
              value={edit.duration_minutes}
              onChange={(e) => setEdit({ ...edit, duration_minutes: Number(e.target.value) })}
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
          <label className="text-sm">
            <span className="text-xs text-foreground/70">Peak viewers</span>
            <input
              type="number"
              min={0}
              value={edit.peak_viewers}
              onChange={(e) => setEdit({ ...edit, peak_viewers: Number(e.target.value) })}
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
          <label className="text-sm sm:col-span-2">
            <span className="text-xs text-foreground/70">Registration link</span>
            <input
              type="url"
              value={edit.registration_link}
              onChange={(e) => setEdit({ ...edit, registration_link: e.target.value })}
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
          <label className="text-sm sm:col-span-2">
            <span className="text-xs text-foreground/70">Join URL</span>
            <input
              type="url"
              value={edit.join_url}
              onChange={(e) => setEdit({ ...edit, join_url: e.target.value })}
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
          <label className="text-sm sm:col-span-2">
            <span className="text-xs text-foreground/70">Description / agenda</span>
            <textarea
              value={edit.description}
              onChange={(e) => setEdit({ ...edit, description: e.target.value })}
              rows={3}
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none"
            />
          </label>

          <div className="sm:col-span-2 space-y-3 rounded-xl border border-dashed border-border p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-primary">Deliverables</p>
            <div>
              <span className="text-xs text-foreground/70">Meeting notes, summary &amp; key Q&amp;As</span>
              <div className="mt-1">
                <RichTextEditor value={edit.notes_html} onChange={(html) => setEdit((s) => ({ ...s, notes_html: html }))} />
              </div>
            </div>
            <label className="text-sm block">
              <span className="text-xs text-foreground/70">Replay video URL (Zoom / YouTube)</span>
              <input
                type="url"
                value={edit.replay_url}
                onChange={(e) => setEdit({ ...edit, replay_url: e.target.value })}
                className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              />
            </label>
            <div className="text-sm">
              <span className="text-xs text-foreground/70">Notes document (PDF / slides)</span>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.ppt,.pptx,.doc,.docx"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setUploading(true);
                    try {
                      const url = await uploadSessionDoc(file);
                      setEdit((s) => ({ ...s, notes_doc_url: url }));
                      toast.success("Document uploaded - save to apply");
                    } catch (err: any) {
                      toast.error("Upload failed", { description: err?.message });
                    } finally {
                      setUploading(false);
                      if (fileRef.current) fileRef.current.value = "";
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-border hover:bg-accent disabled:opacity-60"
                >
                  {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                  Upload document
                </button>
                {edit.notes_doc_url && (
                  <>
                    <a
                      href={edit.notes_doc_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    >
                      <FileText className="h-3.5 w-3.5" /> View attached file
                    </a>
                    <button
                      type="button"
                      onClick={() => setEdit((s) => ({ ...s, notes_doc_url: "" }))}
                      className="text-xs text-muted-foreground hover:text-foreground"
                    >
                      Remove
                    </button>
                  </>
                )}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={edit.notes_published}
                onChange={(e) => setEdit({ ...edit, notes_published: e.target.checked })}
                className="h-4 w-4"
              />
              <span>Publish Notes &amp; Replay to Public Hub</span>
            </label>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={edit.is_published}
              onChange={(e) => setEdit({ ...edit, is_published: e.target.checked })}
              className="h-4 w-4"
            />
            <span>Published</span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="h-4 w-4" />
            <span className="inline-flex items-center gap-1">
              <Send className="h-3.5 w-3.5 text-primary" />
              Email registered students on save
            </span>
          </label>
          <div className="sm:col-span-2">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-cta text-primary-foreground text-sm font-semibold hover:opacity-90 transition disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save changes
            </button>
            <p className="mt-2 text-xs text-foreground/60">
              Emails go out only when status changes to <strong>Scheduled</strong> and the checkbox is on. Every unique
              registered address receives it.
            </p>
          </div>
        </div>
      )}
    </li>
  );
}

function StatusPill({ status }: { status: SessionStatus }) {
  const map: Record<SessionStatus, string> = {
    planning: "bg-blue-100 text-blue-800 border-blue-200",
    scheduled: "bg-emerald-100 text-emerald-800 border-emerald-200",
    completed: "bg-muted text-muted-foreground border-border",
  };
  return (
    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${map[status]}`}>
      {status}
    </span>
  );
}

function csvCell(v: string | number | null | undefined): string {
  let s = v === null || v === undefined ? "" : String(v);
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return `"${s.replace(/"/g, '""')}"`;
}

function AttendeesPanel({
  attendees,
  sessions,
  loading,
  paidPlanByEmail,
}: {
  attendees: Attendee[];
  sessions: Session[];
  loading: boolean;
  paidPlanByEmail: Record<string, string>;
}) {
  const titleById = useMemo(() => Object.fromEntries(sessions.map((s) => [s.id, s.title])), [sessions]);
  const planFor = (email: string) => paidPlanByEmail[(email || "").trim().toLowerCase()] ?? "None";

  // Session filter: "all" | "upcoming" | a specific session id | "general" (no session).
  const [sessionFilter, setSessionFilter] = useState<string>("all");
  const upcomingSession = useMemo(() => {
    const now = Date.now();
    return sessions
      .filter((s) => s.starts_at && new Date(s.starts_at).getTime() > now)
      .sort((a, b) => new Date(a.starts_at!).getTime() - new Date(b.starts_at!).getTime())[0];
  }, [sessions]);
  const filteredAttendees = useMemo(() => {
    if (sessionFilter === "all") return attendees;
    if (sessionFilter === "upcoming")
      return attendees.filter((a) => a.session_id && a.session_id === upcomingSession?.id);
    if (sessionFilter === "general") return attendees.filter((a) => !a.session_id);
    return attendees.filter((a) => a.session_id === sessionFilter);
  }, [attendees, sessionFilter, upcomingSession]);

  // Share of registrations by acquisition channel (within the current filter).
  const sourceMix = useMemo(() => {
    const counts = new Map<string, number>();
    for (const a of filteredAttendees) {
      const label = referralBadge(a.source).label;
      counts.set(label, (counts.get(label) ?? 0) + 1);
    }
    const total = filteredAttendees.length || 1;
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([label, n]) => ({ label, n, pct: Math.round((n / total) * 100) }));
  }, [filteredAttendees]);

  const exportCsv = () => {
    const header = [
      "Name",
      "Email",
      "Phone",
      "Target consulate",
      "Acquisition source",
      "Submitted question",
      "Volunteer",
      "Session attended",
      "Paid plan enrolled",
      "Registered at",
    ];
    const rows = filteredAttendees.map((a) =>
      [
        a.name ?? "",
        a.email,
        a.whatsapp ?? "",
        a.target_consulate ?? "",
        referralBadge(a.source).label,
        a.question ?? "",
        a.volunteer_status ? "Yes" : "No",
        a.session_id ? (titleById[a.session_id] ?? a.session_id) : "General",
        planFor(a.email),
        format(new Date(a.created_at), "yyyy-MM-dd HH:mm"),
      ].map(csvCell),
    );
    const csv = [header.map(csvCell).join(","), ...rows.map((r) => r.join(","))].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const filterSlug =
      sessionFilter === "upcoming"
        ? "upcoming"
        : sessionFilter === "all"
          ? "all"
          : sessionFilter === "general"
            ? "general"
            : (titleById[sessionFilter] ?? "session").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40);
    a.download = `session-attendees-${filterSlug}-${format(new Date(), "yyyy-MM-dd")}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">


      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-semibold inline-flex items-center gap-2">
          <Mail className="h-4 w-4 text-primary" /> Registered attendees
          <span className="text-xs font-normal text-foreground/50">
            ({filteredAttendees.length}{sessionFilter !== "all" ? ` of ${attendees.length}` : ""})
          </span>
        </h2>
        <div className="flex items-center gap-2">
          <select
            value={sessionFilter}
            onChange={(e) => setSessionFilter(e.target.value)}
            className="text-xs rounded-full border border-border bg-background px-3 py-1.5"
          >
            <option value="all">All sessions</option>
            {upcomingSession && <option value="upcoming">Upcoming: {upcomingSession.title}</option>}
            <option value="general">General (no session)</option>
            {sessions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.title}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={exportCsv}
            disabled={filteredAttendees.length === 0}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-border hover:bg-accent disabled:opacity-60"
          >
            <Download className="h-3.5 w-3.5" /> Export CSV
          </button>
        </div>
      </div>

      {sourceMix.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {sourceMix.map((s) => (
            <span
              key={s.label}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 px-3 py-1 text-xs"
            >
              {s.label}
              <strong className="font-semibold">{s.pct}%</strong>
              <span className="text-foreground/50">({s.n})</span>
            </span>
          ))}
        </div>
      )}

      {filteredAttendees.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-foreground/60">
          {attendees.length === 0 ? "No registrations yet." : "No registrations match this filter."}
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase tracking-wider text-foreground/60">
                <tr>
                  <th className="text-left px-4 py-2">Name</th>
                  <th className="text-left px-4 py-2">Email</th>
                  <th className="text-left px-4 py-2">Phone</th>
                  <th className="text-left px-4 py-2">Consulate</th>
                  <th className="text-left px-4 py-2">Source</th>
                  <th className="text-left px-4 py-2">Question</th>
                  <th className="text-left px-4 py-2">Volunteer</th>
                  <th className="text-left px-4 py-2">Session attended</th>
                  <th className="text-left px-4 py-2">Paid plan</th>
                  <th className="text-left px-4 py-2">Registered</th>
                </tr>
              </thead>
              <tbody>
                {filteredAttendees.map((a) => (
                  <tr key={a.id} className="border-t border-border align-top">
                    <td className="px-4 py-2">{a.name || "-"}</td>
                    <td className="px-4 py-2 font-mono text-xs">{a.email}</td>
                    <td className="px-4 py-2">{a.whatsapp || "-"}</td>
                    <td className="px-4 py-2">{a.target_consulate || "-"}</td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${referralBadge(a.source).className}`}
                      >
                        {referralBadge(a.source).label}
                      </span>
                    </td>
                    <td className="px-4 py-2 max-w-[260px]">
                      <span className="line-clamp-3 text-foreground/80">{a.question || "-"}</span>
                    </td>
                    <td className="px-4 py-2">{a.volunteer_status ? "Yes" : "No"}</td>
                    <td className="px-4 py-2 text-foreground/70">
                      {a.session_id ? (titleById[a.session_id] ?? "-") : "General"}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      {planFor(a.email) === "None" ? (
                        <span className="text-foreground/50">None</span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 px-2 py-0.5 text-xs font-medium">
                          {planFor(a.email)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-foreground/60 whitespace-nowrap">
                      {format(new Date(a.created_at), "MMM d, h:mm a")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
